// Ported from deexoptions-mobile-app/components/SurveyComps/SurveyCta.tsx.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  surveyQueryRoot,
  useActiveSurvey,
  useDismissSurvey,
  useRecordSurveyView,
  type SurveyPlacement,
} from "../../hooks/queries/useSurvey";

type Props = {
  placement: SurveyPlacement;
  className?: string;
};

/**
 * The survey prompt for one placement (SETTINGS and TRANSACTION_SUCCESS).
 *
 * Renders nothing unless the backend returns a survey for this exact placement — which is
 * how the admin's placement choice controls where the CTA appears. The same component is
 * mounted at every surface; no surface decides for itself whether it should show.
 */
const SurveyCard: React.FC<Props> = ({ placement, className = "" }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: survey } = useActiveSurvey(placement);
  const recordView = useRecordSurveyView();
  const dismiss = useDismissSurvey();

  const [hidden, setHidden] = useState(false);
  // The view call is counted once per user server-side, but there is no reason to fire it
  // on every re-render either.
  const viewed = useRef<string | null>(null);

  useEffect(() => {
    if (!survey?._id || viewed.current === survey._id) return;
    viewed.current = survey._id;
    recordView.mutate(survey._id);
    // recordView is a stable mutation object; depending on it would re-fire the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey?._id]);

  if (!survey || hidden) return null;

  const onDismiss = () => {
    // Hide immediately — the user asked for it gone, and the call is idempotent, so a
    // failure costs nothing worse than the card returning on a later visit.
    setHidden(true);
    dismiss.mutate(survey._id, {
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: surveyQueryRoot }),
    });
  };

  return (
    <div
      className={`w-full rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1F2C36] ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D4EBFD] text-[18px] dark:bg-secondary_button/20">
          💬
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-sora text-[15px] font-semibold leading-5 text-gray-900 dark:text-gray-200">
            Got a minute? Tell us what you think
          </h4>
          {!!survey.description && (
            <p className="mt-1 text-[13px] leading-5 text-gray-500 dark:text-gray-400">
              {survey.description}
            </p>
          )}
          {!!survey.rewardPoints && (
            <span className="mt-2 inline-block rounded-full bg-[#D4EBFD] px-2.5 py-1 text-[11px] font-bold text-secondary_button dark:bg-secondary_button/20">
              Earn {survey.rewardPoints} points
            </span>
          )}
        </div>
      </div>

      {/* Stacked full-width actions, primary first. */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => navigate(`/survey?placement=${placement}`)}
          className="w-full rounded-lg bg-secondary_button py-3 text-[14px] font-semibold text-white"
        >
          Take survey
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="mt-2.5 w-full rounded-lg border border-gray-200 py-3 text-[14px] font-semibold text-gray-600 dark:border-gray-700 dark:text-gray-300"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default SurveyCard;
